import React, { useEffect, useMemo, useRef } from 'react';
import styles from './Iframe.module.css';
import { getTweetId } from '../urlUtils';
import { cx } from '#/helpers/css';

interface IframeProps {
  embed_html?: string;
}

interface TwitterWidgets {
  createTweet: (tweetId: string, element: HTMLElement, options?: object) => Promise<HTMLElement>;
  load: (element?: HTMLElement) => void;
}

interface TWTTR {
  widgets: TwitterWidgets;
  ready: (cb: (twttr: TWTTR) => void) => void;
}

const TwitterEmbed = ({
  embed_html
}: IframeProps) => {
  const tweetRef = useRef<HTMLDivElement | null>(null);
  const hasRendered = useRef<boolean>(false);
  const tweetId = useMemo(() => {
    return embed_html ? getTweetId(embed_html) : null;
  }, [embed_html]);  

  useEffect(() => {
    if (!tweetId || hasRendered.current) return;
    
    const loadTwitterScript = async () => {
      if (window.twttr) return Promise.resolve(window.twttr);
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://platform.twitter.com/widgets.js';
        script.async = true;
        script.onload = () => resolve(window.twttr);
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    let cancelled = false;
  
    const renderTweet = async () => {
      const twttr = await loadTwitterScript() as TWTTR;
 
      if (!cancelled && twttr?.widgets && tweetRef.current && !tweetRef?.current.innerHTML) {
        await twttr.widgets.createTweet(tweetId, tweetRef?.current);
      }
    };
  
    renderTweet();
  
    return () => {
      cancelled = true;
      if (tweetRef.current) {
        tweetRef.current.innerHTML = '';
      }
    };
  }, [tweetId]);
  
  return (
    <div
      ref={tweetRef}
      className={cx(styles.iframe, styles.twitterIframe)}
    />
  )
}

export default TwitterEmbed;